import { useState, useCallback, useRef } from "react";

export type ToastVariant = "default" | "error" | "success" | "warning";

export type Toast = {
  id: string;
  message: string;
  variant: ToastVariant;
};

type ToastOptions = {
  variant?: ToastVariant;
  /** Duration in ms before auto-dismiss. Defaults to 4000. */
  duration?: number;
};

let globalAddToast: ((message: string, opts?: ToastOptions) => void) | null = null;

/**
 * useToast — lightweight toast notification manager.
 *
 * Returns { toasts, addToast, removeToast }.
 * One instance should live at the app root (inside <Toaster />); consumers
 * can call the module-level `toast()` helper after the root is mounted, or
 * use the hook directly for component-local toasts.
 */
export function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const addToast = useCallback(
    (message: string, opts: ToastOptions = {}) => {
      const id = Math.random().toString(36).slice(2);
      const variant: ToastVariant = opts.variant ?? "default";
      const duration = opts.duration ?? 4000;

      setToasts((prev) => [...prev, { id, message, variant }]);

      const timer = setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
        timers.current.delete(id);
      }, duration);
      timers.current.set(id, timer);
    },
    []
  );

  // Register the global helper when this hook is mounted at the root
  if (globalAddToast === null) {
    globalAddToast = addToast;
  }

  return { toasts, addToast, removeToast };
}

/**
 * Module-level helpers. Safe to call from anywhere after <Toaster /> mounts.
 * Falls back silently if Toaster is not yet mounted.
 */
export const toast = {
  show(message: string, opts?: ToastOptions) {
    globalAddToast?.(message, opts);
  },
  error(message: string, duration?: number) {
    globalAddToast?.(message, { variant: "error", duration });
  },
  success(message: string, duration?: number) {
    globalAddToast?.(message, { variant: "success", duration });
  },
  warning(message: string, duration?: number) {
    globalAddToast?.(message, { variant: "warning", duration });
  },
};
